import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { consolidateGraph } from "@/lib/schema-graph";
import { collectionPageSchema } from "@/lib/aeo";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { caseStudies, categories, type CaseStudy } from "@/data/case-studies";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const PAGE_URL = `${SITE}/case-studies`;
const TITLE = "Case Studies — เคสจริงลูกค้า NYC Language Institute | แต่งงานต่างชาติ · PR · BOI";
const DESC =
  "อ่านเคสจริง 8 เคสที่ NYC Legal & Notary ช่วยลูกค้าผ่าน — เยอรมัน Standesamt · AHPRA Australia PR · UK Spouse Visa · Texas Probate — ส่งภาพเอกสารมาเช็กให้ก่อน ฟรี ไม่คิดค่าปรึกษา";

export const Route = createFileRoute("/case-studies")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/case-studies", "th")},
      { name: "description", content: ctaDesc(DESC, "/case-studies", "th")},
      { property: "og:title", content: ctaTitle(TITLE, "/case-studies", "th")},
      { property: "og:description", content: ctaDesc(DESC, "/case-studies", "th")},
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "article" },
      { name: "twitter:title", content: ctaTitle(TITLE, "/case-studies", "th")},
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }, ...hreflangLinks("/case-studies")],
    scripts: [consolidateGraph([
      collectionPageSchema({ path: "/case-studies", name: TITLE, description: DESC, itemCount: caseStudies.length }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Case Studies", item: PAGE_URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "NYC Legal & Notary — Case Studies",
          itemListElement: caseStudies.map((cs, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Article",
              headline: cs.title,
              about: cs.category,
              articleSection: cs.country,
              author: { "@type": "Organization", name: "NYC Legal & Notary Service" },
            },
          })),
        }),
      },
      // Round 11 — Removed Organization/aggregateRating/Review block.
      // Google policy: ratings must come from real users and pair with
      // verified Review[] on same entity. ItemList of Articles (above) is
      // sufficient and policy-safe for case-study indexing.
    ])],
  }),
  component: CaseStudiesPage,
});

function CaseStudiesPage() {
  const [filter, setFilter] = useState<CaseStudy["category"] | "All">("All");
  const filtered = useMemo(
    () => (filter === "All" ? caseStudies : caseStudies.filter((c) => c.category === filter)),
    [filter],
  );

  return (
    <main className="bg-background">
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-gold/10 via-transparent to-transparent" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <nav className="text-xs text-white/80" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-gold">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-white">Case Studies</span>
          </nav>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-gold">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gold" /> Real Client Stories
          </div>
          <h1 className="mt-5 max-w-3xl text-3xl font-extrabold leading-tight sm:text-4xl lg:text-5xl">
            เคสจริง 4,250+ ราย ใน 15 ปี — เลือกอ่าน 8 เรื่องที่เปลี่ยนชีวิตลูกค้า
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg">
            ทุกเคสมาจากลูกค้าจริง (anonymized ตาม PDPA) บอกครบตั้งแต่ปัญหา ขั้นตอนแก้
            ผลลัพธ์ ค่าใช้จ่าย และไทม์ไลน์ — เพื่อให้คุณเห็นภาพว่า เคสคุณเองจะใช้เวลา/งบประมาณเท่าไร
          </p>
          <div className="quick-answer mt-6 max-w-2xl rounded-2xl border border-gold/40 bg-gold/10 p-5" data-speakable="true" itemScope itemType="https://schema.org/Question">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gold">AI Quick Answer</div>
            <h2 className="text-base font-bold text-white sm:text-lg" itemProp="name">NYC Language Institute มีผลงานเคสจริงเท่าไร?</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="direct-answer mt-2 text-sm leading-relaxed text-white/85" itemProp="text">NYC Language Institute ปิดเคสสำเร็จ 4,250+ ราย ตลอด 15 ปี ใน 50+ ประเทศปลายทาง อัตรา first-time approval 97% ครอบคลุม LTR/Non-B Visa, Apostille, Probate, BOI, Marriage Registration และ Embassy Legalization — เคสจริงทั้งหมดเปิดเผยพร้อมราคา ไทม์ไลน์ และผลลัพธ์ (anonymized ตาม PDPA)</p>
            </div>
          </div>
          <div className="mt-8 grid grid-cols-3 gap-4 sm:max-w-xl">
            {[
              { v: "4,250+", l: "เคสสำเร็จ" },
              { v: "97%", l: "First-time approval" },
              { v: "50+", l: "ประเทศปลายทาง" },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                <div className="text-2xl font-extrabold text-gold sm:text-3xl">{s.v}</div>
                <div className="mt-1 text-[11px] uppercase tracking-wider text-white/80">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category filter */}
      <section className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-2 overflow-x-auto px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={() => setFilter("All")}
            className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition ${
              filter === "All"
                ? "bg-navy-deep text-white"
                : "bg-muted text-foreground hover:bg-muted/70"
            }`}
          >
            ทั้งหมด ({caseStudies.length})
          </button>
          {categories.map((c) => {
            const count = caseStudies.filter((cs) => cs.category === c.key).length;
            if (count === 0) return null;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setFilter(c.key)}
                className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                  filter === c.key
                    ? "bg-navy-deep text-white"
                    : "bg-muted text-foreground hover:bg-muted/70"
                }`}
              >
                <span className="mr-1">{c.icon}</span>
                {c.label} ({count})
              </button>
            );
          })}
        </div>
      </section>

      {/* Cases */}
      <section className="mx-auto max-w-7xl space-y-12 px-4 py-14 sm:px-6 lg:py-20">
        {filtered.map((cs, idx) => (
          <article
            key={cs.slug}
            id={cs.slug}
            className="group grid gap-8 rounded-3xl border border-border bg-card p-6 shadow-sm transition hover:shadow-xl sm:p-8 lg:grid-cols-12 lg:p-10"
          >
            {/* Left rail */}
            <div className="lg:col-span-4">
              <div className="flex items-start gap-3">
                <span className="text-5xl leading-none">{cs.flag}</span>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gold">
                    Case #{String(idx + 1).padStart(2, "0")} · {cs.timeline}
                  </div>
                  <div className="mt-1 text-sm font-semibold text-muted-foreground">
                    {cs.category} → {cs.country}
                  </div>
                </div>
              </div>

              <h2 className="mt-5 text-xl font-extrabold leading-snug text-foreground sm:text-2xl">
                {cs.title}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground">{cs.client}</p>

              <div className="mt-6 grid grid-cols-3 gap-2">
                {cs.metrics.map((m) => (
                  <div key={m.label} className="rounded-xl border border-border bg-muted/30 p-3 text-center">
                    <div className="text-sm font-extrabold text-navy-deep">{m.value}</div>
                    <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                      {m.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex flex-wrap gap-1.5">
                {cs.services.map((s) => (
                  <span
                    key={s}
                    className="rounded-full border border-navy-deep/15 bg-navy-deep/[0.04] px-2.5 py-1 text-[11px] font-medium text-navy-deep"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>

            {/* Right content */}
            <div className="lg:col-span-8">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-destructive">
                  ⚠️ ปัญหา / Challenge
                </div>
                <p className="mt-2 text-sm leading-relaxed text-foreground sm:text-base">
                  {cs.challenge}
                </p>
              </div>

              <div className="mt-6">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-600">
                  🔧 ขั้นตอน NYC / Solution
                </div>
                <ol className="mt-3 space-y-2.5">
                  {cs.solution.map((step, i) => (
                    <li key={i} className="flex gap-3 text-sm text-foreground">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold/20 text-[11px] font-bold text-gold">
                        {i + 1}
                      </span>
                      <span className="pt-0.5 leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="mt-6 rounded-2xl bg-emerald-50 p-4 ring-1 ring-emerald-200">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">
                  ✅ ผลลัพธ์ / Outcome
                </div>
                <p className="mt-1.5 text-sm leading-relaxed text-emerald-900 sm:text-base">
                  {cs.outcome}
                </p>
              </div>

              <blockquote className="mt-6 border-l-4 border-gold bg-muted/30 px-5 py-4">
                <p className="text-sm italic leading-relaxed text-foreground sm:text-base">
                  "{cs.quote.text}"
                </p>
                <footer className="mt-2 text-xs font-semibold text-muted-foreground">
                  — {cs.quote.attribution}
                </footer>
              </blockquote>
            </div>
          </article>
        ))}
      </section>

      {/* CTA */}
      <section className="bg-navy-deep text-white">
        <div className="mx-auto max-w-5xl px-4 py-16 text-center sm:px-6 lg:py-20">
          <h2 className="text-2xl font-extrabold sm:text-3xl lg:text-4xl">
            เคสของคุณจะเป็นเคสถัดไปของเรา
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-white/75">
            ทุกเคสเริ่มจากการคุยฟรี 15 นาที — ส่งเอกสาร screenshot
            มาให้ทีมประเมินทันที ตอบไทม์ไลน์/ค่าใช้จ่ายภายในวันเดียวกัน
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href="tel:+66832494999"
              className="rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-navy-deep shadow-lg shadow-amber-500/30 hover:bg-amber-400"
            >
              📞 083-249-4999
            </a>
            <a
              href="https://line.me/ti/p/@nycli"
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-[#04A03A] px-6 py-3 text-sm font-bold text-white hover:bg-[#05B14C]"
            >
              💬 LINE @nycli
            </a>
            <Link
              to="/pricing"
              className="rounded-full border border-white/30 px-6 py-3 text-sm font-bold text-white hover:bg-white/10"
            >
              ดูตารางราคา →
            </Link>
          </div>
        <RelatedHubGrid locale="th" />
      </div>
      </section>
    </main>
  );
}
